/**
 * What the new job page's header hid, brought back (go core, 11/3):
 *
 *  - The status word, the same word every other screen uses. A held job said
 *    "closeout" before; now it says Held.
 *  - Street address, PO, WO #, water account, tax, and the estimate / invoice
 *    numbers (the classic lineage strip). The office reads these out on calls.
 *  - Status moves in one or two clicks: the same two buttons classic shows for
 *    this status (Hold, Resume, Reschedule…), and every other legal move one
 *    click away under "Move to". Same rules and endpoint as classic.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ban, ChevronDown } from "lucide-react";
import { cn, statusColor } from "@/lib/utils";
import { customerWoNumber } from "@/lib/wo-number";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CancelJobDialog } from "@/components/cancel-job-dialog";
import { ReopenJobDialog } from "@/components/reopen-job-dialog";
import { SendBackToFieldDialog } from "@/components/send-back-to-field-dialog";
import { JobLineageStrip } from "../detail/lineage-strip";
import type { JobDetail, JobDetailResponse } from "../detail/types";
import { primaryHeaderActions, type HeaderAction } from "../jobDetailHelpers";

/** "office_review" → "Office review", the word every status chip in the app shows. */
export function statusWord(status: string | null | undefined): string {
    const s = (status ?? "").replace(/_/g, " ").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "No status";
}

/** The facts the office reads out on calls, in reading order. Empty ones are left out. */
export function vitalsOf(job: JobDetail): Array<{ key: string; text: string; tone?: "warn" | "muted" }> {
    const out: Array<{ key: string; text: string; tone?: "warn" | "muted" }> = [];
    const street = [job.site_address_line, job.site_city]
        .map((x) => x?.trim())
        .filter(Boolean)
        .join(", ");
    if (street) out.push({ key: "address", text: street });
    const po = job.customer_po?.trim();
    if (po) out.push({ key: "po", text: `PO ${po}` });
    const wo = customerWoNumber(job, { includePo: false });
    if (wo) out.push({ key: "wo", text: `WO ${wo}` });
    if (job.water_provider_id || job.water_provider_name) {
        const acct = job.site_water_account_number?.trim();
        if (!job.water_provider_is_portal) out.push({ key: "acct", text: "No account (paper city)", tone: "muted" });
        else if (acct) out.push({ key: "acct", text: `Acct ${acct}` });
        else out.push({ key: "acct", text: "Account # missing", tone: "warn" });
    }
    if (job.customer_tax_status === "exempt") out.push({ key: "tax", text: "Tax exempt", tone: "muted" });
    else if (job.customer_tax_status === "taxable") out.push({ key: "tax", text: "Taxable", tone: "muted" });
    else if (job.customer_tax_status === "unknown") out.push({ key: "tax", text: "Tax status not set", tone: "warn" });
    return out;
}

export function FreshVitals({ data }: { data: JobDetailResponse }) {
    const job = data.job;
    const facts = vitalsOf(job);
    return (
        <div className="mt-2 grid gap-1.5" data-testid="fresh-vitals">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px]">
                <span
                    className={cn("rounded-md border px-2 py-0.5 text-[14px] font-bold", statusColor(job.status))}
                    data-testid="fresh-status"
                >
                    {statusWord(job.status)}
                </span>
                {facts.map((f) => (
                    <span
                        key={f.key}
                        data-testid={`fresh-vital-${f.key}`}
                        className={cn(
                            "tabular-nums",
                            f.tone === "warn" && "font-semibold text-amber-700 dark:text-amber-400",
                            f.tone === "muted" && "text-muted-foreground",
                        )}
                    >
                        {f.text}
                    </span>
                ))}
            </div>
            <JobLineageStrip job={job} invoices={data.invoices ?? []} estimates={data.estimates ?? []} />
        </div>
    );
}

type StatusMutation = { mutate: (status: string) => void; isPending: boolean };

/**
 * The classic header's two buttons for this status, then "Move to" with every
 * other legal move. Hold, Resume, Reschedule, Start… are one or two clicks.
 */
export function FreshStatusMoves({
    data,
    updateStatus,
    onNeedSchedule,
}: {
    data: JobDetailResponse;
    updateStatus: StatusMutation;
    /** "Schedule" on a job with no date opens the schedule editor instead. */
    onNeedSchedule: () => void;
}) {
    const job = data.job;
    const [dialog, setDialog] = useState<null | "cancel" | "reopen" | "send-back">(null);
    const actions = primaryHeaderActions(job.status, {
        jobId: job.id,
        customerId: job.customer_id,
        siteId: job.site_id,
        hasInvoices: (data.invoices ?? []).length > 0,
    });
    // The overflow's "Edit Job" and "Create estimate" live in More; keep the
    // moves (status changes, cancel, links like Create invoice) here.
    const moreMoves = actions.overflow.filter((a) => !(a.kind === "link" && a.icon === "edit"));
    const jobLabel = `Job #${job.display_number || job.id.slice(0, 8)}`;

    const run = (a: HeaderAction) => {
        if (a.kind === "status") {
            if (a.status === "scheduled" && !job.scheduled_start) {
                onNeedSchedule();
                toast.info("Set the date first. The schedule editor is open.");
                return;
            }
            updateStatus.mutate(a.status);
        } else if (a.kind === "cancel") setDialog("cancel");
        else if (a.kind === "send-back") setDialog("send-back");
        else if (a.kind === "reopen") setDialog("reopen");
    };

    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="fresh-status-moves">
            {actions.primary.map((a) =>
                a.kind === "link" ? (
                    <Button key={a.label} size="sm" asChild>
                        <Link to={a.to}>{a.label}</Link>
                    </Button>
                ) : (
                    <Button
                        key={a.label}
                        size="sm"
                        variant={a.label === "Hold" ? "outline" : "default"}
                        disabled={updateStatus.isPending}
                        onClick={() => run(a)}
                        data-testid={`fresh-move-${a.kind === "status" ? a.status : a.kind}`}
                    >
                        {a.label}
                    </Button>
                ),
            )}
            {moreMoves.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="fresh-move-to">
                            Move to <ChevronDown className="ml-1 h-3.5 w-3.5" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        {moreMoves.map((a, i) =>
                            a.kind === "link" ? (
                                <DropdownMenuItem key={`${a.label}-${i}`} asChild>
                                    <Link to={a.to}>{a.label}</Link>
                                </DropdownMenuItem>
                            ) : a.kind === "cancel" ? (
                                <DropdownMenuItem
                                    key="cancel"
                                    className="text-red-600 dark:text-red-400"
                                    onSelect={() => setDialog("cancel")}
                                >
                                    <Ban className="mr-2 h-3.5 w-3.5" /> {a.label}
                                </DropdownMenuItem>
                            ) : (
                                <DropdownMenuItem
                                    key={`${a.kind === "status" ? a.status : a.kind}-${i}`}
                                    disabled={updateStatus.isPending}
                                    onSelect={() => run(a)}
                                >
                                    {a.label}
                                </DropdownMenuItem>
                            ),
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
            <CancelJobDialog
                open={dialog === "cancel"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
            />
            <ReopenJobDialog
                open={dialog === "reopen"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
            />
            <SendBackToFieldDialog
                open={dialog === "send-back"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
                techName={job.assigned_to_name ?? null}
            />
        </div>
    );
}
